/* Core & libraries */
import React, { useEffect, useState, useRef } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

/* Helpers, constants, custom methods, internal libraries */
import { useUserContext } from "../../contexts/userContext";
import { useLoyaltyService } from "../../services/loyaltyService";
import { useLocaleContext } from "../../contexts/localeContext";

/* Components */
import LoyaltyContent from "components/loyaltyContent";
import LoyaltyContentSkeleton from "./skeletonLoyaltyContent";
import LoyaltyBanner from "components/loyaltyBanner";

/* Styles */
import styles from "./styles.js"

const LoyaltyPageHeader = (props) => {
  const { showLoyaltyConsent, onTermsAccepted } = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const userContext = useUserContext();
  const globalLocale = useLocaleContext();
  const { getLoyaltyTermsAccepted, loyaltyAcceptedResponse, getNotificationBanner, showLoyaltyBannerResponse } = useLoyaltyService();

  const [lepTermsAccepted, setLepTermsAccepted] = useState(false);
  const hasFetched = useRef(false);
  const [showLoyaltyBanner, setShowLoyaltyBanner] = useState(false);
  const [notificationBannerData, setNotificationBannerData] = useState(null);

  const companyEntityId = userContext?.userInfo?.companyInfo?.companyEntityId;  
  const lepCanAcceptTerms = userContext?.userInfo?.lepCanAcceptTerms;

  useEffect(() => {
    (async () => {
      if(showLoyaltyBannerResponse) {
        await getLoyaltyBannerData()
        setShowLoyaltyBanner(showLoyaltyBannerResponse);
      }
    })();
  }, [globalLocale, showLoyaltyBannerResponse]);

  useEffect(() => {
    if (companyEntityId && !hasFetched.current) {
      hasFetched.current = true;
      getLoyaltyTermsAccepted(companyEntityId);
    }
  }, [companyEntityId, getLoyaltyTermsAccepted]);

  useEffect(() => {
    if (loyaltyAcceptedResponse !== undefined) {
      setLepTermsAccepted(loyaltyAcceptedResponse);
    }
  }, [loyaltyAcceptedResponse]);

  const getLoyaltyBannerData = async () => {
    const response = await getNotificationBanner();
    if (response) {
      setNotificationBannerData(response)
    }
  }

  const renderLoyaltyContent = () => {
    return (<Container className={classes.container}>
      <Row>
        <Col xs={7} sm={9} className={classes.loyaltyHeaderText}>
          <h1 className={classes.title}>
            <FormattedMessage id="loyalty.title" />
          </h1>
          <p className={classes.description}>
            <FormattedMessage id="loyalty.description" />
          </p>
        </Col>
        {lepTermsAccepted && <Col xs={5} sm={3} className="align-content-end">
          <LoyaltyContent showContent={lepCanAcceptTerms || lepTermsAccepted} onTermsAccepted={onTermsAccepted} showLoyaltyConsent={showLoyaltyConsent} setTermsAccepted={setLepTermsAccepted}/>
        </Col>}
      </Row>
      <Row>
        {lepCanAcceptTerms && !lepTermsAccepted && <Col xs={12} className="align-content-end">
          <LoyaltyContent showContent={lepCanAcceptTerms || lepTermsAccepted} onTermsAccepted={onTermsAccepted} showLoyaltyConsent={showLoyaltyConsent} setTermsAccepted={setLepTermsAccepted}/>
        </Col>}
      </Row>
      {/* This will need BE integration. We will need a flag to show the loyalty banner, the tile and description */}
      {
        lepTermsAccepted && showLoyaltyBanner &&
        <LoyaltyBanner 
          title={notificationBannerData?.title}
          message={notificationBannerData?.message}
          setShowLoyaltyBanner={setShowLoyaltyBanner}
        />
      }
    </Container>)
  }

  const renderLoyaltyContentSkeleton = () => {
    return (<Container className={classes.container}>
      <Row>
        <Col xs={7} sm={9} className={classes.loyaltyHeaderText}>
          <h1 className={classes.title}>
            <FormattedMessage id="loyalty.title" />
          </h1>
          <p className={classes.description}>
            <FormattedMessage id="loyalty.description" />
          </p>
        </Col>
        <Col xs={12}>
          <LoyaltyContentSkeleton />
        </Col>
      </Row>
    </Container>)
  }
  
  return (
    <div className={classes.loyaltyPageHeader}>
      {typeof lepTermsAccepted === "boolean" ? renderLoyaltyContent() : renderLoyaltyContentSkeleton()}
    </div>
  )
}

export default LoyaltyPageHeader;